import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { quizNext, subscribeQuizMaster } from "./quizApiService";
import { getTeacherSession, normalizeCard } from "./quizSession";

export default function QuizTeacherLive() {
  const { t } = useTranslation();
  const { sessionId } = useParams(); // shortId
  const navigate = useNavigate();

  const session = useRef(getTeacherSession(sessionId)).current;
  const cards = session?.cards || [];
  const totalQuestions = cards.length;
  const secondsPerAnswer = session?.secondsPerAnswer || 30;

  const [currentIndex, setCurrentIndex] = useState(-1);
  const [timeLeft, setTimeLeft] = useState(secondsPerAnswer);
  const [answeredUuids, setAnsweredUuids] = useState(() => new Set());
  const [participantCount, setParticipantCount] = useState(session?.participants?.length || 0);
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState(null);

  const didInit = useRef(false);
  const currentCardIdRef = useRef(null);

  const isLastQuestion = currentIndex >= totalQuestions - 1;
  // Memoized so the per-second timer re-render does not recreate the card DOM.
  const currentCard = useMemo(
    () => (currentIndex >= 0 ? normalizeCard(cards[currentIndex]) : null),
    [cards, currentIndex]
  );
  currentCardIdRef.current = currentCard?.cardId || null;

  const questionContent = useMemo(() => {
    if (!currentCard) {
      return (
        <p className="text-lightgray text-center py-6 animate-pulse">
          {t("quiz.loadingQuestion")}
        </p>
      );
    }
    return (
      <>
        <p className="text-sm font-bold text-blue-500 mb-4 uppercase tracking-wider">
          {currentCard.cardName}
        </p>
        <div
          className="text-2xl md:text-xl font-bold text-black leading-relaxed"
          dangerouslySetInnerHTML={{ __html: currentCard.questionText || "" }}
        />
        {currentCard.questionImage && (
          <img
            src={currentCard.questionImage}
            alt=""
            className="mt-4 max-h-60 rounded-xl object-contain border border-blue-50"
          />
        )}
        {currentCard.isChoice && currentCard.options.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
            {currentCard.options.map((opt, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-blue-50 bg-bluelight px-4 py-3 text-base font-medium text-black"
              >
                {opt.text}
              </div>
            ))}
          </div>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard]);

  const finishToResults = useCallback(() => {
    navigate(`/quiz/session/${sessionId}/teacher/results`);
  }, [navigate, sessionId]);

  const advance = useCallback(async () => {
    if (advancing || !session?.uuidQuiz) return;
    setAdvancing(true);
    setError(null);
    try {
      const res = await quizNext({ uuidQuiz: session.uuidQuiz });
      if (res?.status === "FINISHED") {
        finishToResults();
        return;
      }
      const questionsLeft = res?.questionsLeft ?? 0;
      const idx = totalQuestions - questionsLeft - 1;
      setCurrentIndex(idx >= 0 ? idx : 0);
      setAnsweredUuids(new Set());
      setTimeLeft(secondsPerAnswer);
    } catch (err) {
      console.error("Failed to advance quiz:", err);
      setError(err?.response?.data?.data?.error || err?.message || "Failed to advance");
    } finally {
      setAdvancing(false);
    }
  }, [advancing, session, totalQuestions, secondsPerAnswer, finishToResults]);

  // Show the first question on mount (guarded against StrictMode double-run).
  useEffect(() => {
    if (didInit.current) return;
    if (!session?.uuidQuiz) return;
    didInit.current = true;
    advance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Master event stream: count answers for the current question, track late joiners.
  useEffect(() => {
    if (!session?.uuidQuiz) return undefined;
    const unsubscribe = subscribeQuizMaster(session.uuidQuiz, {
      onEvent: (event) => {
        const payload = event.payload || {};
        if (event.eventType === "PARTICIPANT_JOINED_EVENT") {
          setParticipantCount((prev) => prev + 1);
        } else if (event.eventType === "PARTICIPANT_ANSWER_EVENT") {
          if (payload.cardId !== currentCardIdRef.current) return;
          const uuid = payload.uuidParticipant || payload.uuidParticipan;
          if (!uuid) return;
          setAnsweredUuids((prev) => {
            if (prev.has(uuid)) return prev;
            const next = new Set(prev);
            next.add(uuid);
            return next;
          });
        }
      },
    });
    return unsubscribe;
  }, [session]);

  // Display countdown (teacher manually advances).
  useEffect(() => {
    if (currentIndex < 0 || timeLeft <= 0) return undefined;
    const interval = setInterval(() => setTimeLeft((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearInterval(interval);
  }, [timeLeft, currentIndex]);

  if (!session?.uuidQuiz) {
    return (
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="text-center">
          <h2 className="text-xl font-bold text-black mb-2">
            {t("quiz.sessionNotFound") || "Quiz session not found"}
          </h2>
          <button
            onClick={() => navigate("/quiz")}
            className="primary-btn !rounded-full h-11 !py-0 flex items-center justify-center px-7 mx-auto mt-4"
          >
            {t("quiz.backToHome")}
          </button>
        </div>
      </div>
    );
  }

  const answeredCount = answeredUuids.size;
  const totalStudents = Math.max(participantCount, answeredCount);
  const timerPercent = (timeLeft / secondsPerAnswer) * 100;
  const isLowTime = timeLeft <= 5;

  return (
    <div className="flex-1 flex flex-col">
      {/* Top bar */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-blue-50 px-5 py-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="bg-darkblue text-white rounded-full px-4 py-2 text-sm font-semibold shadow-sm">
            {t("quiz.questionOf", {
              current: Math.max(currentIndex + 1, 1),
              total: totalQuestions,
            }) || `${Math.max(currentIndex + 1, 1)} / ${totalQuestions}`}
          </span>
          <span
            className={`text-2xl font-bold flex items-center gap-2 ${
              isLowTime ? "text-red-500 animate-pulse" : "text-black"
            }`}
          >
            {timeLeft}s
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-8">
        <div className="w-full max-w-2xl">
          {/* Circular Timer */}
          <div className="flex justify-center mb-8">
            <div className="relative w-28 h-28">
              <svg className="w-full h-full transform -rotate-90 drop-shadow-sm" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="white" stroke="#F0F1FF" strokeWidth="8" />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="transparent"
                  stroke={isLowTime ? "#EF4444" : "#1E4AD9"}
                  strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 42}
                  strokeDashoffset={2 * Math.PI * 42 * (1 - timerPercent / 100)}
                  className="transition-all duration-1000 ease-linear"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <span className={`text-3xl font-bold ${isLowTime ? "text-red-500" : "text-darkblue"}`}>
                  {timeLeft}
                </span>
              </div>
            </div>
          </div>

          {/* Question card */}
          <div className="rounded-3xl bg-white p-8 shadow-sm border border-blue-50 mb-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-darkblue opacity-20"></div>
            {questionContent}
          </div>

          {/* Answered progress */}
          <div className="rounded-3xl border border-blue-50 shadow-sm p-6 bg-white mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="font-medium text-lightgray flex items-center gap-2">
                👥 {t("quiz.studentsAnswered") || "Students answered"}
              </span>
              <span className="text-lg font-bold text-darkblue bg-blue-50 px-3 py-1 rounded-lg">
                {answeredCount} / {totalStudents || 0}
              </span>
            </div>
            <div className="w-full bg-blue-50 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-400 to-darkblue h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${totalStudents ? (answeredCount / totalStudents) * 100 : 0}%` }}
              />
            </div>
          </div>

          {error && <p className="text-center text-red-500 mb-4 font-medium">{error}</p>}

          {/* Next / Finish button */}
          <div className="text-center">
            <button
              onClick={advance}
              disabled={advancing || currentIndex < 0}
              className={`primary-btn px-10 !rounded-full h-12 !py-0 flex items-center justify-center text-base font-semibold mx-auto ${
                advancing || currentIndex < 0 ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isLastQuestion ? t("quiz.finishQuiz") : t("quiz.nextQuestion")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
